import type { RunSession } from '../../types/ski-data';
import { usePlaybackMachine } from '../../state-machines/usePlaybackMachine';
import { useFrameData } from '../../hooks/useFrameData';
import { useTurnBoundary } from '../../hooks/useTurnBoundary';
import StatusBadge from '../common/StatusBadge';

/**
 * IndicatorDetail - R2 right column (200px).
 *
 * Displays real-time and window-level indicators for the current playback
 * position / current turn:
 *   1. Current turn score (fs-3xl mono) + level badge.
 *   2. Real-time metric cards (speed, edge angle, slope, euler angles) from
 *      the current frame.
 *   3. Window metrics table — 9 B1 metrics across 3 categories (edging /
 *      pressure / turn quality), each with a quality dot.
 *   4. Outside pressure ratio with a 0.75 reference progress bar.
 *   5. Turn shape — roundness + symmetry scores.
 */
interface IndicatorDetailProps {
  runSession: RunSession;
}

/** Convert a quaternion to Euler angles (roll/pitch/yaw) in degrees. */
function quaternionToEuler(q: {
  w: number;
  x: number;
  y: number;
  z: number;
}): { roll: number; pitch: number; yaw: number } {
  const { w, x, y, z } = q;
  const roll = Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y));
  const pitch = Math.asin(Math.max(-1, Math.min(1, 2 * (w * y - z * x))));
  const yaw = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
  return {
    roll: (roll * 180) / Math.PI,
    pitch: (pitch * 180) / Math.PI,
    yaw: (yaw * 180) / Math.PI,
  };
}

/** Resolve a quality dot color from a value + good/ok thresholds. */
function qualityColor(value: number, good: number, ok: number): string {
  if (value >= good) return 'var(--status-good)';
  if (value >= ok) return 'var(--status-ok)';
  return 'var(--status-bad)';
}

export default function IndicatorDetail({ runSession }: IndicatorDetailProps) {
  const playback = usePlaybackMachine();
  const ts = playback.state.currentTimestamp;
  const { frame } = useFrameData(runSession.frames, ts);
  const { currentTurn } = useTurnBoundary(runSession.turns, ts);

  const turn = currentTurn;
  const score = turn ? turn.overall_score : runSession.overall_score;
  const level = turn ? turn.level : runSession.level;
  const euler = quaternionToEuler(frame.quaternion);
  const edgeAngle = Math.max(frame.edge_angle_left, frame.edge_angle_right);
  const b1 = runSession.b1_summary;

  // Real-time metric cards.
  const rtMetrics = [
    { label: '速度', value: `${frame.speed.toFixed(1)}`, unit: 'm/s' },
    { label: '立刃', value: `${edgeAngle.toFixed(0)}`, unit: '°' },
    { label: '坡度', value: `${runSession.a1_summary.slope_angle.toFixed(0)}`, unit: '°' },
    { label: '姿态', value: `${euler.roll.toFixed(0)}/${euler.pitch.toFixed(0)}`, unit: '°' },
  ];

  // Window metrics: 9 B1 metrics in 3 categories, each with a quality dot.
  const windowCategories = [
    {
      title: '立刃',
      items: [
        { label: '平均立刃', value: `${b1.avg_edge_angle.toFixed(0)}°`, color: qualityColor(b1.avg_edge_angle, 25, 18) },
        { label: '最大立刃', value: `${b1.max_edge_angle.toFixed(0)}°`, color: qualityColor(b1.max_edge_angle, 30, 22) },
        { label: '同步率', value: `${b1.edging_sync_rate.toFixed(0)}`, color: qualityColor(b1.edging_sync_rate, 80, 60) },
      ],
    },
    {
      title: '压力',
      items: [
        { label: '外板占比', value: `${(b1.avg_outside_pressure * 100).toFixed(0)}%`, color: qualityColor(b1.avg_outside_pressure, 0.75, 0.6) },
        { label: '转移时间', value: `${b1.pressure_transfer_time.toFixed(0)}ms`, color: b1.pressure_transfer_time <= 150 ? 'var(--status-good)' : b1.pressure_transfer_time <= 250 ? 'var(--status-ok)' : 'var(--status-bad)' },
        { label: '压力稳定', value: `${b1.pressure_stability.toFixed(0)}`, color: qualityColor(b1.pressure_stability, 80, 60) },
      ],
    },
    {
      title: '弯形',
      items: [
        { label: '卡宾比例', value: `${(b1.carving_ratio * 100).toFixed(0)}%`, color: qualityColor(b1.carving_ratio, 0.4, 0.2) },
        { label: '圆润度', value: `${b1.turn_roundness_avg.toFixed(0)}`, color: qualityColor(b1.turn_roundness_avg, 80, 60) },
        { label: '对称度', value: `${b1.turn_symmetry_avg.toFixed(0)}`, color: qualityColor(b1.turn_symmetry_avg, 80, 60) },
      ],
    },
  ];

  // Outside pressure ratio for the current turn.
  const opRatio = turn ? turn.outside_pressure_ratio : b1.avg_outside_pressure;
  const opPct = Math.round(opRatio * 100);
  const opColor = opRatio >= 0.75 ? 'var(--status-good)' : opRatio >= 0.6 ? 'var(--status-ok)' : 'var(--status-bad)';

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: 'var(--space-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-md)',
      }}
    >
      {/* Score + level */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)' }}>
        <span
          style={{
            fontSize: 'var(--fs-3xl)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-primary)',
            lineHeight: 1,
          }}
        >
          {score.toFixed(0)}
        </span>
        <StatusBadge level={level} size="sm" />
      </div>

      {/* Real-time metric cards (2×2) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-xs)',
        }}
      >
        {rtMetrics.map((m) => (
          <div
            key={m.label}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-xs) var(--space-sm)',
            }}
          >
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)' }}>
              {m.label}
            </div>
            <div
              style={{
                fontSize: 'var(--fs-sm)',
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-primary)',
              }}
            >
              {m.value}
              <span style={{ color: 'var(--text-tertiary)', marginLeft: 2 }}>{m.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Window metrics table */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-xs)',
        }}
      >
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)' }}>
          窗口指标
        </div>
        {windowCategories.map((cat) => (
          <div key={cat.title}>
            <div
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-secondary)',
                marginBottom: '2px',
              }}
            >
              {cat.title}
            </div>
            {cat.items.map((it) => (
              <div
                key={it.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  fontSize: 'var(--fs-xs)',
                  padding: '1px 0',
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: it.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1, color: 'var(--text-tertiary)' }}>{it.label}</span>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                  {it.value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Outside pressure ratio with 0.75 reference */}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-tertiary)',
            marginBottom: '4px',
          }}
        >
          <span>外板压力</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: opColor }}>{opPct}%</span>
        </div>
        <div
          style={{
            position: 'relative',
            height: '6px',
            background: 'var(--bg-surface-1)',
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${opPct}%`,
              background: opColor,
              borderRadius: 'var(--radius-full)',
            }}
          />
        </div>
        {/* 0.75 reference marker */}
        <div
          style={{
            position: 'relative',
            height: '0',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '75%',
              top: '-8px',
              width: '1px',
              height: '8px',
              background: 'var(--text-tertiary)',
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: '75%',
              top: '2px',
              transform: 'translateX(-50%)',
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            0.75
          </span>
        </div>
      </div>

      {/* Turn shape: roundness + symmetry */}
      {turn && (
        <div>
          <div
            style={{
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-tertiary)',
              marginBottom: '4px',
            }}
          >
            弯形
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <ShapeBar
              label="圆润度"
              value={turn.turn_roundness}
              color={qualityColor(turn.turn_roundness, 80, 60)}
            />
            <ShapeBar
              label="对称度"
              value={turn.turn_symmetry}
              color={qualityColor(turn.turn_symmetry, 80, 60)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Small horizontal bar for roundness/symmetry scores. */
function ShapeBar({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', width: '40px', flexShrink: 0 }}>
        {label}
      </span>
      <div
        style={{
          flex: 1,
          height: '4px',
          background: 'var(--bg-surface-1)',
          borderRadius: 'var(--radius-full)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: '100%',
            background: color,
            borderRadius: 'var(--radius-full)',
          }}
        />
      </div>
      <span
        style={{
          fontSize: 'var(--fs-xs)',
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-secondary)',
          width: '24px',
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {value.toFixed(0)}
      </span>
    </div>
  );
}
